import { Loader } from "@fanduel/formation-react-components";
import {
  SelectFieldProps,
  Box,
  Flex,
  LabelText,
  BodyText,
} from "@fanduel/formation-react-components";
import { ArrowsArrowDown } from "@fanduel/formation-react-icons";
import { tokenMappers } from "@fanduel/formation-shared-utils";
import React from "react";
import { useUncontrolled } from "@/utils/hooks/useUncontrolled";
import styles from "./style.module.scss";

type optionGroup = {
  label: string;
  options: NonNullable<SelectFieldProps["options"]>;
};

const SelectField = React.forwardRef<
  HTMLSelectElement,
  SelectFieldProps & {
    onValueChange?: (value: string) => void;
    optGroups?: optionGroup[];
    /** Icon to display to the left of the button label */
    iconLeft?: React.ReactElement;
    /** Icon to display to the right of the button label */
    iconRight?: React.ReactElement;
    loading?: boolean;
  }
>(
  (
    {
      defaultValue = "",
      label,
      helperText,
      errorMessage,
      css,
      isCondensed,
      isRequired,
      isOptional,
      isInvalid,
      isDisabled,
      value,
      id,
      onBlur,
      onFocus,
      onValueChange,
      options = [],
      optGroups,
      placeholder,
      loading,
      ...rest
    },
    ref
  ) => {
    const [_value, handleChange] = useUncontrolled({
      value,
      defaultValue,
      onChange: onValueChange,
    });
    const [isFocused, setIsFocused] = React.useState(false);

    const handleFocus = (evt: React.FocusEvent<HTMLSelectElement>) => {
      onFocus?.(evt);
      setIsFocused(true);
    };

    const handleBlur = (evt: React.FocusEvent<HTMLSelectElement>) => {
      onBlur?.(evt);
      setIsFocused(false);
    };

    const tokenName = React.useMemo(() => {
      if (isFocused) return tokenMappers.input("active");
      if (isInvalid || errorMessage) return tokenMappers.input("error");
      if (isDisabled) return tokenMappers.input("disabled");
      return tokenMappers.input("base");
    }, [isFocused, isInvalid, errorMessage, isDisabled]);

    return (
      <Box
        css={css}
        borderWidth={tokenName.borderWidth}
        borderStyle="solid"
        borderRadius={tokenName.borderRadius}
        borderColor={tokenName.borderColor}
        bg={tokenName.backgroundColor}
        minWidth={0}
        className={styles.selectBox}
      >
        <Flex
          direction="column"
          py="space3"
          px="space4"
          gap="space1"
          {...(isCondensed && {
            py: "space2",
            px: "space3",
          })}
        >
          <Flex align="baseline" justify="space-between" mb="space1">
            <LabelText
              as="label"
              htmlFor={id}
              size="md"
              color={tokenName.label}
            >
              {label}
              {isRequired && (
                <LabelText
                  as="span"
                  color="systemImportantContentAccent"
                  ml="space1"
                >
                  * Required
                </LabelText>
              )}
            </LabelText>
            {isOptional && (
              <BodyText size="sm" color={tokenName.optional}>
                Optional
              </BodyText>
            )}
          </Flex>

          <Flex
            align="center"
            justifyContent="space-between"
            gap="space2"
            position="relative"
          >
            <Box
              ref={ref}
              aria-required={isRequired}
              color={
                placeholder && _value === ""
                  ? tokenName.placeholder
                  : tokenName.content
              }
              disabled={isDisabled}
              value={_value}
              as="select"
              id={id}
              data-testid={id}
              backgroundColor="transparent"
              border="none"
              outline="none"
              fontFamily={tokenName.typography}
              fontSize={tokenName.typography}
              fontWeight={tokenName.typography}
              letterSpacing={tokenName.typography}
              lineHeight={tokenName.typography}
              flex="1"
              maxWidth="100%"
              p={0}
              onChange={(evt) =>
                handleChange((evt.target as HTMLSelectElement).value)
              }
              {...(!isDisabled && {
                onFocus: handleFocus,
                onBlur: handleBlur,
              })}
              css={{ appearance: "none" }}
              {...(isDisabled && {
                cursor: "not-allowed",
              })}
              {...rest}
            >
              {placeholder && (
                <option value="" disabled>
                  {placeholder}
                </option>
              )}

              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {optGroups
                ? optGroups?.map((group) => (
                    <React.Fragment key={group.label}>
                      <optgroup label={group.label}>
                        {group.options?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                    </React.Fragment>
                  ))
                : null}
            </Box>
            <Box
              color={isDisabled ? "contentDisabled" : "contentSubtle"}
              position="absolute"
              right={0}
              top={0}
              css={() => ({
                cursor: "pointer",
                pointerEvents: "none",
                ...css,
              })}
            >
              {loading ? <Loader size="sm" /> : <ArrowsArrowDown />}
            </Box>
          </Flex>

          {errorMessage && (
            <Box>
              <BodyText
                color="componentDataInputContentError"
                marginTop="space1"
              >
                {errorMessage}
              </BodyText>
            </Box>
          )}
        </Flex>
        {helperText && (
          <Box
            borderTopWidth={tokenName.borderWidth}
            borderBottomLeftRadius={tokenName.borderRadius}
            borderBottomRightRadius={tokenName.borderRadius}
            borderTopColor={tokenName.borderColor}
            bg={tokenName.helperText.backgroundColor}
            borderTopStyle="solid"
            px="space4"
            py="space1"
            {...(isCondensed && {
              px: "space3",
            })}
          >
            <BodyText size="sm" color={tokenName.helperText.content}>
              {helperText}
            </BodyText>
          </Box>
        )}
      </Box>
    );
  }
);

SelectField.displayName = "SelectField";

export type { SelectFieldProps };
export { SelectField };
